'use client';

import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  startTransition
} from 'react';

const VOLUME_LIMITS = {
  MIN: 0,
  MAX: 100,
  DEFAULT: 80
} as const;

const PLAYBACK_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  PLAYING: 'playing',
  PAUSED: 'paused',
  ERROR: 'error',
  BUFFERING: 'buffering',
  ENDED: 'ended'
} as const;

const PLAYER_EVENTS = {
  PLAY: 'play',
  PAUSE: 'pause',
  SEEK: 'seek',
  VOLUME_CHANGE: 'volume_change',
  MUTE: 'mute',
  UNMUTE: 'unmute',
  ERROR: 'error',
  MEDIA_LOAD: 'media_load',
  MEDIA_END: 'media_end'
} as const;

export type MediaType = 'audio' | 'video' | 'stream' | 'podcast' | 'radio' | 'article';
export type PlaybackState = typeof PLAYBACK_STATES[keyof typeof PLAYBACK_STATES];
export type PlayerEvent = typeof PLAYER_EVENTS[keyof typeof PLAYER_EVENTS];

export interface MediaMetadata {
  readonly id: string;
  readonly type: MediaType;
  readonly title: string;
  readonly cover?: string;
  readonly duration?: number;
  readonly isLive?: boolean;
  readonly format?: string;
  readonly description?: string;
}

export interface PlaybackPosition {
  readonly currentTime: number;
  readonly duration: number;
  readonly buffered: number;
  readonly seekable: ReadonlyArray<{ start: number; end: number }>;
}

export interface VolumeState {
  readonly level: number;
  readonly isMuted: boolean;
  readonly previousLevel: number;
}

export interface MediaQuality {
  readonly bitrate: number;
  readonly resolution?: string;
  readonly codec?: string;
  readonly isAdaptive: boolean;
}

export interface NetworkState {
  readonly connectionType: 'wifi' | 'cellular' | 'unknown';
  readonly isOnline: boolean;
  readonly downloadSpeed?: number;
}

export interface MediaState {
  readonly isVisible: boolean;
  readonly isMinimized: boolean;
  readonly isFullscreen: boolean;
  readonly playbackState: PlaybackState;
  readonly currentMedia: string | null;
  readonly metadata: MediaMetadata | null;
  readonly position: PlaybackPosition;
  readonly volume: VolumeState;
  readonly quality: MediaQuality | null;
  readonly network: NetworkState;
  readonly error: string | null;
  readonly persistentPlayback: boolean;
  readonly autoplay: boolean;
  readonly shuffle: boolean;
  readonly repeat: 'none' | 'one' | 'all';
  readonly playbackRate: number;
  readonly crossfadeEnabled: boolean;
  readonly lastUpdated: number;
  readonly sessionId: string;
}

export interface AnalyticsEvent {
  readonly event: PlayerEvent;
  readonly mediaId?: string;
  readonly mediaType?: MediaType;
  readonly timestamp: number;
  readonly sessionId: string;
  readonly userId?: string;
  readonly customParameters?: Record<string, string | number | boolean>;
}

export type AnalyticsCallback = (event: AnalyticsEvent) => void;

type MediaAction =
  | { type: 'PLAY_MEDIA'; payload: { url: string; metadata: MediaMetadata } }
  | { type: 'TOGGLE_PLAY_PAUSE' }
  | { type: 'SET_PLAYBACK_STATE'; payload: PlaybackState }
  | { type: 'UPDATE_POSITION'; payload: Partial<PlaybackPosition> }
  | { type: 'SET_VOLUME'; payload: number }
  | { type: 'TOGGLE_MUTE' }
  | { type: 'SEEK_TO'; payload: number }
  | { type: 'SET_PLAYBACK_RATE'; payload: number }
  | { type: 'MINIMIZE_PLAYER' }
  | { type: 'MAXIMIZE_PLAYER' }
  | { type: 'TOGGLE_FULLSCREEN' }
  | { type: 'CLOSE_PLAYER' }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_METADATA'; payload: Partial<MediaMetadata> }
  | { type: 'UPDATE_QUALITY'; payload: MediaQuality }
  | { type: 'UPDATE_NETWORK'; payload: Partial<NetworkState> }
  | { type: 'SET_REPEAT'; payload: 'none' | 'one' | 'all' }
  | { type: 'TOGGLE_SHUFFLE' }
  | { type: 'TOGGLE_CROSSFADE' }
  | { type: 'SET_SETTINGS'; payload: Partial<Pick<MediaState, 'persistentPlayback' | 'autoplay'>> }
  | { type: 'RESET_SESSION' };

const createVolumeState = (level: number, isMuted: boolean, previousLevel: number): VolumeState => ({
  level: Math.max(VOLUME_LIMITS.MIN, Math.min(VOLUME_LIMITS.MAX, Math.round(level))),
  isMuted,
  previousLevel: Math.max(VOLUME_LIMITS.MIN, Math.min(VOLUME_LIMITS.MAX, previousLevel))
});

const createPlaybackPosition = (
  currentTime: number = 0,
  duration: number = 0,
  buffered: number = 0,
  seekable: ReadonlyArray<{ start: number; end: number }> = []
): PlaybackPosition => ({
  currentTime: Math.max(0, currentTime),
  duration: Math.max(0, duration),
  buffered: Math.max(0, Math.min(buffered, duration)),
  seekable: seekable.filter(range => range.start <= range.end)
});

const createNetworkState = (): NetworkState => ({
  connectionType: 'unknown',
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true
});

const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

const createInitialState = (): MediaState => ({
  isVisible: false,
  isMinimized: false,
  isFullscreen: false,
  playbackState: PLAYBACK_STATES.IDLE,
  currentMedia: null,
  metadata: null,
  position: createPlaybackPosition(),
  volume: createVolumeState(VOLUME_LIMITS.DEFAULT, false, VOLUME_LIMITS.DEFAULT),
  quality: null,
  network: createNetworkState(),
  error: null,
  persistentPlayback: false,
  autoplay: false,
  shuffle: false,
  repeat: 'none',
  playbackRate: 1.0,
  crossfadeEnabled: false,
  lastUpdated: Date.now(),
  sessionId: generateSessionId()
});

function mediaReducer(state: MediaState, action: MediaAction): MediaState {
  const timestamp = Date.now();

  const createNewState = (updates: Partial<MediaState>): MediaState => ({
    ...state,
    ...updates,
    lastUpdated: timestamp,
  });

  switch (action.type) {
    case 'PLAY_MEDIA': {
      const { url, metadata } = action.payload;

      if (state.currentMedia === url && state.playbackState === PLAYBACK_STATES.PLAYING) {
        return state;
      }

      return createNewState({
        currentMedia: url,
        metadata,
        isVisible: true,
        isMinimized: false,
        playbackState: PLAYBACK_STATES.LOADING,
        error: null,
        position: createPlaybackPosition(),
        sessionId: state.currentMedia !== url ? generateSessionId() : state.sessionId,
      });
    }

    case 'TOGGLE_PLAY_PAUSE': {
      const newState = state.playbackState === PLAYBACK_STATES.PLAYING
        ? PLAYBACK_STATES.PAUSED
        : PLAYBACK_STATES.PLAYING;

      return createNewState({
        playbackState: newState,
      });
    }

    case 'SET_PLAYBACK_STATE': {
      if (state.playbackState === action.payload) {
        return state;
      }

      return createNewState({
        playbackState: action.payload,
      });
    }

    case 'UPDATE_POSITION': {
      const newPosition = createPlaybackPosition(
        action.payload.currentTime ?? state.position.currentTime,
        action.payload.duration ?? state.position.duration,
        action.payload.buffered ?? state.position.buffered,
        action.payload.seekable ?? state.position.seekable
      );

      const timeDiff = Math.abs(newPosition.currentTime - state.position.currentTime);
      if (timeDiff < 0.1 && newPosition.duration === state.position.duration) {
        return state;
      }

      return createNewState({
        position: newPosition,
        playbackState: state.playbackState === PLAYBACK_STATES.LOADING
          ? PLAYBACK_STATES.PLAYING
          : state.playbackState,
      });
    }

    case 'SET_VOLUME': {
      const newLevel = Math.max(VOLUME_LIMITS.MIN, Math.min(VOLUME_LIMITS.MAX, action.payload));

      if (newLevel === state.volume.level) {
        return state;
      }

      return createNewState({
        volume: createVolumeState(
          newLevel,
          newLevel === 0,
          state.volume.level
        ),
      });
    }

    case 'TOGGLE_MUTE': {
      const { volume } = state;

      return createNewState({
        volume: createVolumeState(
          volume.isMuted ? volume.previousLevel : 0,
          !volume.isMuted,
          volume.isMuted ? volume.previousLevel : volume.level
        ),
      });
    }

    case 'SEEK_TO': {
      const seekTime = Math.max(0, Math.min(state.position.duration, action.payload));

      return createNewState({
        position: {
          ...state.position,
          currentTime: seekTime,
        },
      });
    }

    case 'SET_PLAYBACK_RATE': {
      const rate = Math.max(0.25, Math.min(4.0, action.payload));

      if (rate === state.playbackRate) {
        return state;
      }

      return createNewState({
        playbackRate: rate,
      });
    }

    case 'MINIMIZE_PLAYER':
      return createNewState({
        isMinimized: true,
        isVisible: false,
      });

    case 'MAXIMIZE_PLAYER':
      return createNewState({
        isMinimized: false,
        isVisible: true,
        isFullscreen: false,
      });

    case 'TOGGLE_FULLSCREEN':
      return createNewState({
        isFullscreen: !state.isFullscreen,
        isVisible: !state.isFullscreen,
      });

    case 'CLOSE_PLAYER':
      return state.persistentPlayback
        ? createNewState({
          isVisible: false,
          isMinimized: true,
        })
        : createInitialState();

    case 'SET_ERROR': {
      if (state.error === action.payload) {
        return state;
      }

      return createNewState({
        error: action.payload,
        playbackState: action.payload ? PLAYBACK_STATES.ERROR : state.playbackState,
      });
    }

    case 'CLEAR_ERROR':
      return createNewState({
        error: null,
        playbackState: state.playbackState === PLAYBACK_STATES.ERROR
          ? PLAYBACK_STATES.IDLE
          : state.playbackState,
      });

    case 'UPDATE_METADATA': {
      if (!state.metadata) return state;

      return createNewState({
        metadata: {
          ...state.metadata,
          ...action.payload,
        },
      });
    }

    case 'UPDATE_QUALITY':
      return createNewState({
        quality: action.payload,
      });

    case 'UPDATE_NETWORK':
      return createNewState({
        network: {
          ...state.network,
          ...action.payload,
        },
      });

    case 'SET_REPEAT':
      return createNewState({
        repeat: action.payload,
      });

    case 'TOGGLE_SHUFFLE':
      return createNewState({
        shuffle: !state.shuffle,
      });

    case 'TOGGLE_CROSSFADE':
      return createNewState({
        crossfadeEnabled: !state.crossfadeEnabled,
      });

    case 'SET_SETTINGS':
      return createNewState(action.payload);

    case 'RESET_SESSION':
      return createNewState({
        sessionId: generateSessionId(),
      });

    default:
      return state;
  }
}

export interface MediaContextValue {
  readonly state: MediaState;
  readonly actions: {
    readonly playMedia: (url: string, metadata: MediaMetadata) => void;
    readonly togglePlayPause: () => void;
    readonly setPlaybackState: (state: PlaybackState) => void;
    readonly updatePosition: (position: Partial<PlaybackPosition>) => void;
    readonly seekTo: (time: number) => void;
    readonly setVolume: (volume: number) => void;
    readonly toggleMute: () => void;
    readonly setPlaybackRate: (rate: number) => void;
    readonly minimizePlayer: () => void;
    readonly maximizePlayer: () => void;
    readonly toggleFullscreen: () => void;
    readonly closePlayer: () => void;
    readonly setError: (error: string | null) => void;
    readonly clearError: () => void;
    readonly updateMetadata: (metadata: Partial<MediaMetadata>) => void;
    readonly updateQuality: (quality: MediaQuality) => void;
    readonly updateNetwork: (network: Partial<NetworkState>) => void;
    readonly setRepeat: (repeat: 'none' | 'one' | 'all') => void;
    readonly toggleShuffle: () => void;
    readonly toggleCrossfade: () => void;
    readonly updateSettings: (settings: Partial<Pick<MediaState, 'persistentPlayback' | 'autoplay'>>) => void;
    readonly resetSession: () => void;
  };
  readonly selectors: {
    readonly isPlaying: boolean;
    readonly isLoading: boolean;
    readonly isBuffering: boolean;
    readonly canPlay: boolean;
    readonly hasError: boolean;
    readonly progressPercent: number;
    readonly volumePercent: number;
    readonly isLiveStream: boolean;
    readonly remainingTime: number;
    readonly canSeek: boolean;
    readonly bufferHealth: number;
  };
  readonly analytics: {
    readonly setCallback: (callback: AnalyticsCallback | null) => void;
    readonly trackEvent: (event: PlayerEvent, customParams?: Record<string, string | number | boolean>) => void;
  };
}

const MediaContext = createContext<MediaContextValue | null>(null);

interface MediaProviderProps {
  children: ReactNode;
  initialAnalyticsCallback?: AnalyticsCallback;
  userId?: string;
}

export function MediaProvider({ children, initialAnalyticsCallback, userId }: MediaProviderProps) {
  const [state, dispatch] = useReducer(mediaReducer, createInitialState());
  const analyticsCallbackRef = useRef<AnalyticsCallback | null>(initialAnalyticsCallback || null);
  const userIdRef = useRef(userId);
  const actionsRef = useRef<MediaContextValue['actions']>(undefined);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  const trackEvent = useCallback((event: PlayerEvent, customParams?: Record<string, string | number | boolean>) => {
    if (!analyticsCallbackRef.current) return;

    const analyticsEvent: AnalyticsEvent = {
      event,
      mediaId: state.metadata?.id,
      mediaType: state.metadata?.type,
      timestamp: Date.now(),
      sessionId: state.sessionId,
      userId: userIdRef.current,
      customParameters: customParams,
    };

    try {
      analyticsCallbackRef.current(analyticsEvent);
    } catch (error) {
      console.warn('Analytics callback error:', error);
    }
  }, [state.metadata?.id, state.metadata?.type, state.sessionId]);

  if (!actionsRef.current) {
    actionsRef.current = {
      playMedia: (url: string, metadata: MediaMetadata) => {
        startTransition(() => {
          dispatch({ type: 'PLAY_MEDIA', payload: { url, metadata } });
          trackEvent(PLAYER_EVENTS.MEDIA_LOAD, { url, title: metadata.title });
        });
      },

      togglePlayPause: () => {
        const event = state.playbackState === PLAYBACK_STATES.PLAYING
          ? PLAYER_EVENTS.PAUSE
          : PLAYER_EVENTS.PLAY;

        dispatch({ type: 'TOGGLE_PLAY_PAUSE' });
        trackEvent(event, { currentTime: state.position.currentTime });
      },

      setPlaybackState: (playbackState: PlaybackState) => {
        dispatch({ type: 'SET_PLAYBACK_STATE', payload: playbackState });
      },

      updatePosition: (position: Partial<PlaybackPosition>) => {
        dispatch({ type: 'UPDATE_POSITION', payload: position });
      },

      setVolume: (volume: number) => {
        dispatch({ type: 'SET_VOLUME', payload: volume });
        trackEvent(PLAYER_EVENTS.VOLUME_CHANGE, { volume });
      },

      toggleMute: () => {
        const event = state.volume.isMuted ? PLAYER_EVENTS.UNMUTE : PLAYER_EVENTS.MUTE;
        dispatch({ type: 'TOGGLE_MUTE' });
        trackEvent(event, { volume: state.volume.level });
      },

      seekTo: (time: number) => {
        const oldTime = state.position.currentTime;
        dispatch({ type: 'SEEK_TO', payload: time });
        trackEvent(PLAYER_EVENTS.SEEK, { fromTime: oldTime, toTime: time });
      },

      setPlaybackRate: (rate: number) => {
        dispatch({ type: 'SET_PLAYBACK_RATE', payload: rate });
      },

      minimizePlayer: () => {
        dispatch({ type: 'MINIMIZE_PLAYER' });
      },

      maximizePlayer: () => {
        dispatch({ type: 'MAXIMIZE_PLAYER' });
      },

      toggleFullscreen: () => {
        dispatch({ type: 'TOGGLE_FULLSCREEN' });
      },

      closePlayer: () => {
        dispatch({ type: 'CLOSE_PLAYER' });
      },

      setError: (error: string | null) => {
        dispatch({ type: 'SET_ERROR', payload: error });
        if (error) {
          trackEvent(PLAYER_EVENTS.ERROR, { error });
        }
      },

      clearError: () => {
        dispatch({ type: 'CLEAR_ERROR' });
      },

      updateMetadata: (metadata: Partial<MediaMetadata>) => {
        dispatch({ type: 'UPDATE_METADATA', payload: metadata });
      },

      updateQuality: (quality: MediaQuality) => {
        dispatch({ type: 'UPDATE_QUALITY', payload: quality });
      },

      updateNetwork: (network: Partial<NetworkState>) => {
        dispatch({ type: 'UPDATE_NETWORK', payload: network });
      },

      setRepeat: (repeat: 'none' | 'one' | 'all') => {
        dispatch({ type: 'SET_REPEAT', payload: repeat });
      },

      toggleShuffle: () => {
        dispatch({ type: 'TOGGLE_SHUFFLE' });
      },

      toggleCrossfade: () => {
        dispatch({ type: 'TOGGLE_CROSSFADE' });
      },

      updateSettings: (settings: Partial<Pick<MediaState, 'persistentPlayback' | 'autoplay'>>) => {
        dispatch({ type: 'SET_SETTINGS', payload: settings });
      },

      resetSession: () => {
        dispatch({ type: 'RESET_SESSION' });
      },
    };
  }

  const selectors = useMemo(() => ({
    isPlaying: state.playbackState === PLAYBACK_STATES.PLAYING,
    isLoading: state.playbackState === PLAYBACK_STATES.LOADING,
    isBuffering: state.playbackState === PLAYBACK_STATES.BUFFERING,
    canPlay: state.currentMedia !== null && state.playbackState !== PLAYBACK_STATES.ERROR,
    hasError: state.error !== null,
    progressPercent: state.position.duration > 0
      ? (state.position.currentTime / state.position.duration) * 100
      : 0,
    volumePercent: state.volume.isMuted ? 0 : state.volume.level,
    isLiveStream: state.metadata?.isLive ?? false,
    remainingTime: Math.max(0, state.position.duration - state.position.currentTime),
    canSeek: state.position.seekable.length > 0 && !state.metadata?.isLive,
    bufferHealth: state.position.duration > 0
      ? (state.position.buffered / state.position.duration) * 100
      : 0,
  }), [state.playbackState, state.currentMedia, state.error, state.position, state.volume, state.metadata]);

  const analytics = useMemo(() => ({
    setCallback: (callback: AnalyticsCallback | null) => {
      analyticsCallbackRef.current = callback;
    },
    trackEvent,
  }), [trackEvent]);

  const contextValue = useMemo(() => ({
    state,
    actions: actionsRef.current!,
    selectors,
    analytics,
  }), [state, selectors, analytics]);

  return (
    <MediaContext.Provider value={contextValue}>
      {children}
    </MediaContext.Provider>
  );
}

export function useMediaPlayer() {
  const context = useContext(MediaContext);

  if (!context) {
    throw new Error('useMediaPlayer must be used within a MediaProvider');
  }

  return context;
}

export function useMediaPlayerState() {
  const { state } = useMediaPlayer();
  return state;
}

export function useMediaPlayerActions() {
  const { actions } = useMediaPlayer();
  return actions;
}

export function useMediaPlayerSelectors() {
  const { selectors } = useMediaPlayer();
  return selectors;
}

export function useMediaPlayerAnalytics() {
  const { analytics } = useMediaPlayer();
  return analytics;
}

export function useMediaPlayerProgress() {
  const { state, selectors } = useMediaPlayer();

  return useMemo(() => ({
    currentTime: state.position.currentTime,
    duration: state.position.duration,
    progressPercent: selectors.progressPercent,
    remainingTime: selectors.remainingTime,
    canSeek: selectors.canSeek,
    isLive: selectors.isLiveStream,
  }), [state.position, selectors.progressPercent, selectors.remainingTime, selectors.canSeek, selectors.isLiveStream]);
}

export function useMediaPlayerVolume() {
  const { state, actions } = useMediaPlayer();

  return useMemo(() => ({
    level: state.volume.level,
    isMuted: state.volume.isMuted,
    setVolume: actions.setVolume,
    toggleMute: actions.toggleMute,
  }), [state.volume, actions.setVolume, actions.toggleMute]);
}

export function useMediaPlayerPerformance() {
  const { state } = useMediaPlayer();
  const renderCount = useRef(0);

  useEffect(() => {
    renderCount.current += 1;
  });

  return useMemo(() => ({
    renderCount: renderCount.current,
    lastUpdated: state.lastUpdated,
    timeSinceLastUpdate: Date.now() - state.lastUpdated,
    sessionId: state.sessionId,
    bufferHealth: state.position.duration > 0
      ? (state.position.buffered / state.position.duration) * 100
      : 0,
  }), [state.lastUpdated, state.sessionId, state.position.buffered, state.position.duration]);
}